import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link } from 'react-router';
import { BarChart3, MessageSquare, Search } from '@/components/icons';
import { RUN_STATUSES, type RunStatus } from '@1qa/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { runSearchText } from '@/lib/format';
import { filterByOwner, useOwnerMode } from '@/lib/owner';
import type { Run } from '@/lib/types';
import { DeletableRunsTable } from '@/components/report/DeletableRunsTable';
import { OwnerFilter } from '@/components/OwnerFilter';
import { Button, Card, EmptyState, ErrorNotice, Input, PageHeader, PageLoader, statusLabel } from '@/components/ui';

type Filter = 'ALL' | RunStatus;

export function ReportsPage() {
  const reports = useQuery({
    queryKey: ['reports'],
    queryFn: api.reports,
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING') ? 3000 : false),
  });
  const { user, memberships, canModify } = useAuth();
  const unread = useQuery({ queryKey: ['comments-unread'], queryFn: api.unreadComments });
  // A run's org comes with the report list; without it, fall back to the current org.
  // Only the person who started a run (or a Project Admin) deletes its report.
  const canDeleteRun = useCallback((r: Run) => canModify('report.delete', r.triggeredBy?.id, r.project?.orgId ?? undefined), [canModify]);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [query, setQuery] = useState('');
  const [ownerMode, setOwnerMode] = useOwnerMode();
  // View Only users never start runs - everything they see was shared with them, so no "Mine" filter.
  const canStartRuns = memberships.some((m) => m.role !== 'VIEWER');
  const owner = canStartRuns ? ownerMode : 'all';

  const allRuns = useMemo(() => reports.data ?? [], [reports.data]);
  const myRuns = useMemo(() => filterByOwner(allRuns, (r) => r.triggeredBy?.id, user?.id, 'mine'), [allRuns, user?.id]);
  const runs = owner === 'mine' ? myRuns : allRuns;
  const counts = useMemo(() => {
    const m = new Map<Filter, number>([['ALL', runs.length]]);
    for (const r of runs) m.set(r.status, (m.get(r.status) ?? 0) + 1);
    return m;
  }, [runs]);

  const visible = runs.filter(
    (r) => (filter === 'ALL' || r.status === filter) && (!query || runSearchText(r).includes(query.trim().replace(/^#/, '').toLowerCase())),
  );

  const filters: Filter[] = ['ALL', ...RUN_STATUSES.filter((s) => counts.get(s) || s === filter)];

  return (
    <div>
      <PageHeader title="Reports" description="Shows the runs you started. Switch to Everyone to see all reports in your projects, including ones shared with you." />
      {unread.data && unread.data.total > 0 && (
        <div role="status" className="mb-5 rounded-2xl bg-brand-50 p-4 text-sm text-brand-900 ring-1 ring-brand-200 dark:bg-brand-950/40 dark:text-brand-100 dark:ring-brand-900">
          <p className="flex items-center gap-2 font-medium">
            <MessageSquare className="size-4" /> New comments on {unread.data.runs.length === 1 ? 'a report' : `${unread.data.runs.length} reports`}
          </p>
          <ul className="mt-2 space-y-1">
            {unread.data.runs.map((r) => (
              <li key={r.runId}>
                <Link to={`/runs/${r.runId}`} className="underline underline-offset-2 hover:no-underline">
                  {r.projectName}: {r.count} new from {r.from}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {reports.isPending ? (
        <PageLoader />
      ) : reports.isError ? (
        <ErrorNotice error={reports.error} onRetry={() => reports.refetch()} />
      ) : allRuns.length === 0 ? (
        <EmptyState
          icon={<BarChart3 className="size-6" />}
          title="No reports yet"
          description="When a test runs, or someone shares a report with you, it shows up here."
        />
      ) : (
        <Card>
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
            {canStartRuns && (
              <OwnerFilter value={owner} onChange={setOwnerMode} mineCount={myRuns.length} allCount={allRuns.length} mineLabel="Started by me" />
            )}
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={clsx(
                    'rounded-full px-3 py-[5px] text-[11px]/[16.5px] font-semibold tracking-label uppercase ring-1 ring-inset transition',
                    filter === f
                      ? 'bg-primary-subtle text-primary ring-primary'
                      : 'bg-surface text-fg ring-border hover:bg-fg/8',
                  )}
                >
                  {f === 'ALL' ? 'All' : statusLabel(f)} <span className="opacity-70">{counts.get(f) ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="relative ml-auto w-full sm:w-64">
              <Search className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
              <Input aria-label="Search by project, test name or run ID" placeholder="Search project, test or run ID…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10" />
            </div>
          </div>
          {visible.length === 0 ? (
            owner === 'mine' && myRuns.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-500">
                <p>You haven't started any runs yet.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setOwnerMode('all')}>
                  Show everyone's reports
                </Button>
              </div>
            ) : (
              <p className="px-5 py-10 text-center text-sm text-slate-500">No reports match this filter.</p>
            )
          ) : (
            <DeletableRunsTable runs={visible} canDelete={canDeleteRun} />
          )}
        </Card>
      )}
    </div>
  );
}
